import { ActivityLevel } from "@/clients/models/ActivityLevel";
import { QueryKeys } from "@/constants/query-keys";
import { useGetActivityLevel } from "@/hooks/requests/acivity/use-get-activity-level";
import { useUpdateActivityLevel } from "@/hooks/requests/acivity/use-update-activity-level";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { IonContent, IonPage } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { activityLevelOptions } from "../../hooks/use-activity-level";
import { OnboardingNextFab } from "../../components/onboarding-next-fab";
import { OnboardingStepShell } from "../../components/onboarding-step-shell";
import { SelectablePill } from "../../components/selectable-pill";

const OnboardingActivityPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { activityLevel } = useGetActivityLevel();
  const queryClient = useQueryClient();
  const { updateActivityLevelAsync, isUpdating } = useUpdateActivityLevel();
  const { showError } = useToast();
  const hydratedRef = useRef(false);
  const [selected, setSelected] = useState<ActivityLevel.activityLevel | null>(null);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (activityLevel?.activityLevel) {
      setSelected(activityLevel.activityLevel);
      hydratedRef.current = true;
    }
  }, [activityLevel?.activityLevel]);

  const handleSubmit = async () => {
    if (!selected || isUpdating) return;
    try {
      await Promise.all([
        preloadNextRoute(),
        updateActivityLevelAsync({ activityLevel: selected }),
      ]);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.ACTIVITY_LEVEL] });
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <OnboardingStepShell
          icon="/assets/images/onboarding-icons/activity.svg"
          title="How active are you?"
          subtitle="More activity usually means your body needs more calories"
        >
          <div className="flex w-full flex-col items-center gap-[18px]">
            {activityLevelOptions.map((option) => (
              <SelectablePill
                key={option.value}
                label={option.title}
                description={option.description}
                variant={selected === option.value ? "selected" : "default"}
                onClick={() => setSelected(option.value)}
                disabled={isUpdating}
              />
            ))}
          </div>
          <OnboardingNextFab
            visible={selected !== null}
            onClick={handleSubmit}
            disabled={isUpdating}
          />
        </OnboardingStepShell>
      </IonContent>
    </IonPage>
  );
};

export default OnboardingActivityPage;
